import React, { useState, useEffect, useMemo } from 'react'
import { Button, Modal, GridColumn, Grid, Icon } from 'semantic-ui-react'
import { useSelector, useDispatch } from 'react-redux'
import _ from 'lodash'
import { useQueryClient } from '@tanstack/react-query'

import { Close } from 'components/Icons'
import ProfileInitialBadge from 'components/ProfileInitialBadge'
import { buildDependentAccountsModalColorMap } from 'helpers/account/profileBadgeUtils'

import {
  getDependentDetails,
  getCurPatientData,
  getCurPatientId
} from 'stores/Account/Select'
import {
  getConsentPatientId,
  isPatientIdInConsentList
} from 'helpers/account/patientSwitchList'
import accountActions from 'stores/Account/Actions'
import useMediaQuery from 'hooks/useMediaQuery'
import { useTheme } from 'contexts/ThemeProvider'

import themeStyles from './styles'
import { localization } from './localization'

import AppointmentActions from '@web/stores/Appointments/Actions'
import FhirActions from '@enlyt/stores/Fhir/Actions'
import FhirEnhancedActions from '@web/stores/FhirEnhanced/Actions'
import TestResultActions from '@web/stores/TestResults/Actions'
import { appointmentsKeys } from '@web/hooks/api/appointments'

import {
  observation,
  observationSearchLoading
} from 'stores/FhirEnhanced/Select'
import { getStartDate, getEndDate } from 'stores/TestResults/Select'
import moment from 'moment'
import withLocalization from '@web/helpers/localizationUtils'

const DependentItem = props => {
  const { theme } = useTheme()
  const styles = themeStyles(theme)
  const t = _.get(props, 't')
  const firstname = _.get(props, 'firstname')
  const lastname = _.get(props, 'lastname')
  const isUser = _.get(props, 'isUser')
  const patientId = _.get(props, 'patientId')
  const backgroundColor = _.get(props, 'backgroundColor')
  const closeModal = _.get(props, 'closeModal')
  const curPatient = useSelector(getCurPatientData)
  const isOnSelected = _.isEqual(
    getConsentPatientId(curPatient),
    getConsentPatientId(props)
  )
  const dispatch = useDispatch()
  const { isMobile } = useMediaQuery()
  const birthDate = _.get(props, 'birthDate', '-')
  const queryClient = useQueryClient()
  const clearHealthData = () => {
    dispatch(FhirActions.medicationRequestSearchSuccess(null))
    dispatch(FhirActions.allergyIntoleranceSearchSuccess(null))
    dispatch(FhirActions.immunizationSearchSuccess(null))
    dispatch(FhirActions.conditionSearchSuccess(null))
    dispatch(TestResultActions.setStartDate(null))
    dispatch(TestResultActions.setEndDate(null))

    // Clear cached appointments TanStack queries for the previous patient
    queryClient.removeQueries({ queryKey: appointmentsKeys.all })

    dispatch(AppointmentActions.setApptsOfCheckinLoading(null))

    dispatch(FhirEnhancedActions.clearFhirDataExceptCache())
  }

  const isObservationLoading = useSelector(observationSearchLoading)
  const observationData = useSelector(observation)
  const filterStartDate = useSelector(getStartDate)
  const filterEndDate = useSelector(getEndDate)
  const queryStartDateStr = _.isEmpty(filterStartDate)
    ? ''
    : moment(filterStartDate).utc().format('YYYY-MM-DD')
  const queryEndDateStr = _.isEmpty(filterEndDate)
    ? ''
    : moment(filterEndDate).utc().format('YYYY-MM-DD')
  const queryStart = queryStartDateStr ? `&date=ge${queryStartDateStr}` : ''
  const queryEnd = queryEndDateStr ? `&date=le${queryEndDateStr}` : ''
  const queryFHIR = '_count=50'

  const onSelectHandle = () => {
    if (!isOnSelected) {
      closeModal()
      clearHealthData()
      dispatch(accountActions.setCurPatientId(patientId))
      dispatch(accountActions.getPersonalContactDetails())
      if (
        !isObservationLoading &&
        patientId &&
        !observationData &&
        (queryStart || queryEnd)
      ) {
        dispatch(
          FhirEnhancedActions.observationSearchSwitchFilterDate(
            patientId,
            queryFHIR,
            queryStart,
            queryEnd
          )
        )
        dispatch(
          FhirEnhancedActions.observationSearchSwitchAll(patientId, queryFHIR)
        )
      }
    }
  }

  return (
    <button
      style={styles.dependentItem(isOnSelected, isMobile)}
      onClick={onSelectHandle}
      tabIndex={0}
      autoFocus={props.index === 0}
    >
      <div style={styles.positionRelative(isMobile)}>
        <div style={styles.imgBOx(isOnSelected)}>
          <ProfileInitialBadge
            firstname={firstname}
            lastname={lastname}
            backgroundColor={backgroundColor}
            size='lg'
          />
        </div>
        {isOnSelected && (
          <div style={styles.checkCircle}>
            <Icon name='check' style={styles.checkIcon} />
          </div>
        )}
      </div>
      <div style={styles.fontBold}>
        {firstname?.toUpperCase()} {lastname?.toUpperCase()}{' '}
        {isUser ? t('self') : ''}
      </div>
      <div style={styles.fontThin}>
        {t('DOB')}: {birthDate}
      </div>
    </button>
  )
}

function Index(props) {
  const [open, setOpen] = useState(false)
  const { theme } = useTheme()
  const styles = themeStyles(theme)
  const dispatch = useDispatch()
  const { switchButtonStyle = {}, t } = props
  // eslint-disable-next-line react-hooks/exhaustive-deps -- selector expression is stable enough; suppress hook dep heuristic
  const data = useSelector(getDependentDetails) || []
  const curPatientId = useSelector(getCurPatientId)
  const badgeColorsByPatientId = useMemo(
    () => buildDependentAccountsModalColorMap(data),
    [data]
  )
  const isShowScrollFlag = data.length > 3
  const TriggerButton = () => {
    if (data.length < 2) {
      return null
    }
    return (
      <Button
        tabIndex='0'
        onClick={() => setOpen(true)}
        style={styles.patientSwitch(switchButtonStyle)}
      >
        {t('switchPatient')}
      </Button>
    )
  }
  useEffect(() => {
    if (!isPatientIdInConsentList(data, curPatientId)) {
      if (_.isEmpty(data)) {
        dispatch(accountActions.setCurPatientId(''))
      } else {
        dispatch(
          accountActions.setCurPatientId(
            getConsentPatientId(_.first(data)) || ''
          )
        )
      }
    }
  }, [curPatientId, data, dispatch])
  return (
    <Modal
      centered
      className='patientSwitchModal modalCentered'
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
      open={open}
      trigger={<TriggerButton />}
      style={styles.modal}
    >
      <Modal.Header style={styles.header}>
        <div>{t('modalTitle')}</div>
        <Close
          color={theme.primary}
          style={styles.closeIcon}
          onClick={() => setOpen(false)}
        />
      </Modal.Header>
      <Modal.Content
        style={styles.content(isShowScrollFlag)}
        className='patientSwitchContent'
      >
        <Grid columns={3}>
          {data.map((item, index) => {
            return (
              <GridColumn
                style={styles.gridColumn}
                key={getConsentPatientId(item) ?? index}
              >
                <DependentItem
                  closeModal={() => setOpen(false)}
                  {...item}
                  backgroundColor={badgeColorsByPatientId[item.patientId]}
                  index={index}
                  t={t}
                />
              </GridColumn>
            )
          })}
        </Grid>
      </Modal.Content>
    </Modal>
  )
}

export default withLocalization(Index, localization)
